"use client";

import React from "react";
import {
  Calendar,
  Clock,
  User,
  ArrowLeft,
  ChevronRight,
  Share2,
} from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import type { Post } from "@/integrations/wordpress/types";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

declare global {
  interface Window {
    wvcClient?: {
      getCurrentWPQueryParams: () => Record<string, unknown>;
    };
  }
}

function PostConsumer() {
  const { posts, loading, error, refetch } = usePosts();
  const isGerman = typeof window !== "undefined" && window.location.pathname.replace(/\/$/, "") === "/transfer-hurghada-el-gouna";

  if (loading) {
    return (
      <div className="mx-auto max-w-[1136px] px-6 py-16 animate-pulse">
        <div className="h-4 w-32 rounded bg-muted mb-4" />
        <div className="h-10 w-3/4 rounded bg-muted mb-6" />
        <div className="h-4 w-48 rounded bg-muted mb-8" />
        <div className="aspect-[16/9] w-full rounded-md bg-muted mb-10" />
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-muted" />
          <div className="h-4 w-5/6 rounded bg-muted" />
          <div className="h-4 w-4/6 rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-[1136px] px-6 py-20 text-center">
        <h2 className="text-xl font-semibold text-foreground mb-3">Unable to display post</h2>
        <p className="text-muted-foreground text-sm mb-6">{error}</p>
        <Button variant="default" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const post: Post | undefined = posts?.[0];

  if (!post) {
    return (
      <div className="mx-auto max-w-[1136px] px-6 py-20 text-center">
        <h2 className="text-2xl font-semibold text-foreground mb-3">Post not found</h2>
        <p className="text-muted-foreground text-sm mb-6">
          The requested transfer dispatch or route guide is no longer available.
        </p>
        <Button asChild variant="default">
          <Link href={isGerman ? "/home-de/" : "/"}>Return to home</Link>
        </Button>
      </div>
    );
  }

  // Format date helper
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString(isGerman ? "de-DE" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "Verified route update";

  const authorName = post.author?.name || "Operations Team";
  const authorInitials = authorName
    .split(" ")
    .map((chunk) => chunk[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const authorAvatar =
    post.author?.avatar?.["96"] ||
    post.author?.avatar?.["48"] ||
    post.author?.avatar?.["24"];

  const relatedPosts = posts.slice(1, 4);

  return (
    <article className="mx-auto max-w-[1136px] px-6">
      {/* Breadcrumb & Navigation */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 pt-4">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Link href={isGerman ? "/home-de/" : "/"} className="hover:text-foreground transition-colors">{isGerman ? "Startseite" : "Home"}</Link>
          <ChevronRight className="size-3.5 opacity-60" />
          <span className="text-foreground">{isGerman ? "Reise- und Transferinformationen" : "Route & Travel Updates"}</span>
        </nav>
        <Button asChild variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
          <Link href={isGerman ? "/home-de/" : "/"}>
            <ArrowLeft className="size-3.5" />{isGerman ? "Zurück zur Übersicht" : "Back to overview"}</Link>
        </Button>
      </div>

      {/* Prominent Title & Meta */}
      <header className="mb-8">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {post.categories.map((cat) => (
              <Badge
                key={cat.id}
                variant="secondary"
                className="bg-secondary text-secondary-foreground border border-border text-xs px-2.5 py-0.5 rounded-md"
              >
                {cat.name}
              </Badge>
            ))}
          </div>
        )}

        <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl font-default">
          {post.title}
        </h1>

        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground sm:text-sm">
          <div className="flex items-center gap-1.5">
            <User className="size-4 text-primary" />
            <span className="font-medium text-foreground">{authorName}</span>
          </div>
          <span className="hidden sm:inline-block text-border" aria-hidden="true">•</span>
          <div className="flex items-center gap-1.5">
            <Calendar className="size-4 text-primary" />
            <time dateTime={post.date}>{formattedDate}</time>
          </div>
          <span className="hidden sm:inline-block text-border" aria-hidden="true">•</span>
          <div className="flex items-center gap-1.5">
            <Clock className="size-4 text-primary" />
            <span>{isGerman ? "Informationen zum Tür-zu-Tür-Transfer" : "Door-to-door transit notes"}</span>
          </div>
        </div>

        {/* Signature hairline rule with turquoise terminal dot */}
        <div className="mt-6 relative flex items-center">
          <div className="h-px w-full bg-border" />
          <div className="size-1.5 rounded-full bg-primary shrink-0" />
        </div>
      </header>

      {/* Featured Image */}
      {post.featuredImage ? (
        <div className="mb-10 overflow-hidden rounded-md border border-border bg-card shadow-xs">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full aspect-[16/9] object-cover max-h-[520px]"
          />
        </div>
      ) : (
        <div className="mb-10 overflow-hidden rounded-md border border-border bg-card">
          <img
            src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1810038/img/hurghada_transit_fleet_standard.png"
            data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1810038/img/hurghada_transit_fleet_standard.png-wvc-srcset"
            data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1810038/img/hurghada_transit_fleet_standard.png-wvc-sizes"
            alt="Hurghada Transfer Hub private vehicle on Egyptian route"
            loading="lazy"
            decoding="async"
            className="w-full aspect-[16/9] object-cover max-h-[520px]"
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="mx-auto max-w-[820px]">
        {post.content ? (
          <div
            className="prose prose-neutral max-w-none text-foreground/90 font-default text-base leading-relaxed [&>p]:mb-5 [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:mt-8 [&>h2]:mb-4 [&>h2]:text-foreground [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:mt-6 [&>h3]:mb-3 [&>h3]:text-foreground [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-5 [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:mb-5 [&>blockquote]:border-l-2 [&>blockquote]:border-primary [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:my-6 [&>a]:text-primary [&>a]:underline hover:[&>a]:text-primary/80"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        ) : post.excerpt ? (
          <div className="prose prose-neutral max-w-none text-foreground/90 font-default text-base leading-relaxed">
            <p className="text-lg leading-relaxed text-foreground">{post.excerpt}</p>
            <p className="mt-4 text-muted-foreground">
              Hurghada Transfer Hub operates scheduled and private fixed-price transfers across Hurghada Airport,
              El Gouna, Makadi Bay, Soma Bay, Luxor, and Cairo with transparent fleet management and fixed prices.
            </p>
          </div>
        ) : (
          <p className="text-muted-foreground italic">No article content available.</p>
        )}

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-8 pt-6 border-t border-border flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Tags:</span>
            {post.tags.map((tag) => (
              <Badge key={tag.id} variant="outline" className="text-xs rounded-md border-border text-foreground">
                #{tag.name}
              </Badge>
            ))}
          </div>
        )}

        {/* Action / Share Row */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-md border border-border bg-card p-4 shadow-xs">
          <div>
            <p className="text-sm font-semibold text-foreground">{isGerman ? "Planen Sie diesen Transfer?" : "Planning this transfer?"}</p>
            <p className="text-xs text-muted-foreground">{isGerman ? "Festpreise inklusive Maut- und Parkgebühren. Wir überwachen Ihre Ankunft und mögliche Flugverspätungen kostenlos. Bis zu 60 Minuten Wartezeit ab der vereinbarten Treffzeit sind inklusive." : "Fixed rates with all tolls and parking included. We monitor your flight's arrival time and any delays free of charge, with up to 60 minutes of free waiting time from the agreed meeting time."}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="default" size="sm" className="bg-primary text-white hover:bg-primary/90 font-medium">
              <Link href={isGerman ? "/book-transfer-de/" : "/book-transfer/"}>{isGerman ? "Transfer anfragen" : "Book this route"}</Link>
            </Button>
          </div>
        </div>

        {/* Author Information */}
        <div className="mt-12 rounded-md border border-border bg-card p-6 shadow-xs">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Avatar className="size-14 border border-border">
              {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
              <AvatarFallback className="bg-secondary text-secondary-foreground font-semibold text-base">
                {authorInitials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-foreground font-default">{authorName}</h3>
                <span className="text-xs text-muted-foreground">{isGerman ? "Autor und Disposition" : "Author & Dispatch Supervisor"}</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{isGerman ? "Das Team von Hurghada Transfer Hub informiert über Transfers, Strecken und die Anreise zu den Urlaubsorten am Roten Meer." : "Hurghada Transfer Hub route operations team providing verified logistical insights, highway conditions, and travel advice for arrivals across Red Sea destinations."}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Related Posts Section */}
      {relatedPosts.length > 0 && (
        <section className="mt-16 pt-10 border-t border-border" aria-label="Related route updates">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground tracking-tight">{isGerman ? "Weitere Transferinformationen" : "Related route updates"}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">{isGerman ? "Entdecken Sie weitere Transferinformationen und Reisetipps" : "Explore additional transfer information and travel details"}</p>
            </div>
            <Button asChild variant="ghost" size="sm" className="text-primary hover:text-primary/90 text-xs">
              <Link href={isGerman ? "/travel-guides-de/" : "/travel-guides/"}>{isGerman ? "Alle Artikel ansehen" : "View all articles"}</Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPosts.map((relatedPost, i) => (
              <article
                key={relatedPost.id}
                data-index={i}
                className="group flex flex-col overflow-hidden rounded-md border border-border bg-card shadow-xs hover:border-primary transition-colors"
              >
                {relatedPost.featuredImage ? (
                  <div className="aspect-[16/10] overflow-hidden bg-muted">
                    <img
                      src={relatedPost.featuredImage}
                      alt={relatedPost.title}
                      loading="lazy"
                      decoding="async"
                      className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/10] overflow-hidden bg-muted">
                    <img
                      src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1810038/img/hurghada_transit_fleet_standard.png"
                      data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1810038/img/hurghada_transit_fleet_standard.png-wvc-srcset"
                      data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1810038/img/hurghada_transit_fleet_standard.png-wvc-sizes"
                      alt={relatedPost.title}
                      loading="lazy"
                      decoding="async"
                      className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-5">
                  <span className="text-xs text-muted-foreground mb-2">
                    {relatedPost.date
                      ? new Date(relatedPost.date).toLocaleDateString(isGerman ? "de-DE" : "en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "Route Update"}
                  </span>
                  <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {relatedPost.title}
                  </h3>
                  {relatedPost.excerpt && (
                    <p className="mt-2 text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                      {relatedPost.excerpt}
                    </p>
                  )}
                  <div className="mt-auto pt-4">
                    <Button asChild variant="outline" size="sm" className="w-full text-xs h-8">
                      <Link to={relatedPost.link || "/"}>{isGerman ? "Artikel lesen" : "Read article"}</Link>
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

export default function SinglePost() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : { post_type: "post", per_page: 4 };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-post-template-section"
      data-nav="light"
      className="bg-background text-foreground py-12 md:py-16"
    >
      <PostsProvider className="w-full" wp_query={wp_query}>
        <PostConsumer />
      </PostsProvider>
    </section>
  );
}